import { getSandwormState, setSandwormState } from './desertState.js';

export function updateSandwormState(deltaTime, width, height) {
  const sandwormState = getSandwormState();
  sandwormState.timer += deltaTime;
  
  // Random chance to spawn sandworm (very infrequent)
  if (!sandwormState.active && sandwormState.timer > 12) {
    const spawnChance = 0.0008;
    if (Math.random() < spawnChance) {
      setSandwormState({
        active: true,
        position: Math.random() < 0.5 ? -150 : width + 150,
        direction: sandwormState.position < 0 ? 1 : -1,
        burrowDepth: 0,
        timer: 0
      });
    }
  }
  
  // Update active sandworm
  if (sandwormState.active) {
    sandwormState.position += sandwormState.direction * 120 * deltaTime;
    sandwormState.burrowDepth = Math.sin(sandwormState.timer * 2.5) * 0.6 + 0.4;
    
    // Update segments for massive creature
    updateSandwormSegments(width, height);
    
    // Remove sandworm when it goes off screen
    if (sandwormState.position < -250 || sandwormState.position > width + 250) {
      setSandwormState({
        active: false,
        timer: 0
      });
    }
  }
}

function updateSandwormSegments(width, height) {
  const sandwormState = getSandwormState();
  const segmentCount = sandwormState.segments.length;
  const baseY = height * 0.75;
  
  for (let i = 0; i < segmentCount; i++) {
    const segment = sandwormState.segments[i];
    const segmentOffset = i * 15; // Spacing between segments
    
    segment.x = sandwormState.position - (segmentOffset * sandwormState.direction);
    segment.y = baseY + Math.sin(sandwormState.timer * 3 + i * 0.3) * 25 + sandwormState.burrowDepth * 40;
    segment.size = 45 - (i * 1.5); // Tapering body
    segment.angle = Math.atan2(
      sandwormState.segments[Math.max(0, i - 1)]?.y - segment.y || 0,
      sandwormState.segments[Math.max(0, i - 1)]?.x - segment.x || 1
    );
  }
}

export function drawMassiveSandworm(ctx, width, height, time) {
  const sandwormState = getSandwormState();
  if (!sandwormState.active) return;
  
  ctx.save();
  
  const segments = sandwormState.segments;
  
  // Draw segments from tail to head
  for (let i = segments.length - 1; i >= 0; i--) {
    const segment = segments[i];
    if (segment.size <= 0) continue;
    
    const isHead = i === 0;
    const segmentHue = 18 + i * 1.5;
    
    // Segment shadow
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.beginPath();
    ctx.ellipse(segment.x + 6, segment.y + 6, segment.size * 0.9, segment.size * 0.4, segment.angle, 0, Math.PI * 2);
    ctx.fill();
    
    // Main segment body with massive scale
    const gradient = ctx.createRadialGradient(
      segment.x - segment.size * 0.3, segment.y - segment.size * 0.2, 0,
      segment.x, segment.y, segment.size
    );
    gradient.addColorStop(0, `hsl(${segmentHue}, 65%, 55%)`);
    gradient.addColorStop(0.3, `hsl(${segmentHue + 8}, 60%, 42%)`);
    gradient.addColorStop(0.7, `hsl(${segmentHue + 15}, 55%, 32%)`);
    gradient.addColorStop(1, `hsl(${segmentHue + 22}, 50%, 22%)`);
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.ellipse(segment.x, segment.y, segment.size, segment.size * 0.6, segment.angle, 0, Math.PI * 2);
    ctx.fill();
    
    // Segment ridges and texture
    for (let ridge = 0; ridge < 3; ridge++) {
      const ridgeRadius = segment.size * (0.9 - ridge * 0.2);
      ctx.strokeStyle = `hsl(${segmentHue + 25}, 40%, ${28 - ridge * 4}%)`;
      ctx.lineWidth = 2 + ridge;
      ctx.beginPath();
      ctx.ellipse(segment.x, segment.y, ridgeRadius, ridgeRadius * 0.6, segment.angle, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // Head details for the massive creature
    if (isHead) {
      // Glowing maw
      ctx.save();
      ctx.shadowColor = 'rgba(255, 80, 0, 0.8)';
      ctx.shadowBlur = 25;
      
      const mawGradient = ctx.createRadialGradient(segment.x, segment.y, 0, segment.x, segment.y, segment.size * 0.7);
      mawGradient.addColorStop(0, `hsl(12, 90%, 65%)`);
      mawGradient.addColorStop(0.5, `hsl(8, 85%, 45%)`);
      mawGradient.addColorStop(1, `hsl(5, 80%, 25%)`);
      
      ctx.fillStyle = mawGradient;
      ctx.beginPath();
      ctx.ellipse(segment.x, segment.y, segment.size * 0.7, segment.size * 0.4, segment.angle, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      
      // Massive teeth/spikes around the maw
      for (let tooth = 0; tooth < 12; tooth++) {
        const toothAngle = (tooth / 12) * Math.PI * 2;
        const toothBaseX = segment.x + Math.cos(toothAngle + segment.angle) * segment.size * 0.6;
        const toothBaseY = segment.y + Math.sin(toothAngle + segment.angle) * segment.size * 0.35;
        const toothTipX = segment.x + Math.cos(toothAngle + segment.angle) * segment.size * 0.95;
        const toothTipY = segment.y + Math.sin(toothAngle + segment.angle) * segment.size * 0.55;
        
        ctx.strokeStyle = `hsl(${segmentHue + 30}, 50%, 35%)`;
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(toothBaseX, toothBaseY);
        ctx.lineTo(toothTipX, toothTipY);
        ctx.stroke();
      }
    }
  }
  
  ctx.restore();
}

export function getSandwormEffect(barX, barWidth, barHeight) {
  const sandwormState = getSandwormState();
  if (!sandwormState.active) return 1;
  
  const wormX = sandwormState.position;
  const wormWidth = 200; // Massive effect area
  
  // Check if sandworm is affecting this bar
  if (wormX >= barX - wormWidth/2 && wormX <= barX + barWidth + wormWidth/2) {
    const distanceFromCenter = Math.abs(wormX - (barX + barWidth/2));
    const effect = 1 - (distanceFromCenter / (wormWidth/2));
    return Math.max(0.05, 1 - effect * sandwormState.burrowDepth * 0.9);
  }
  
  return 1;
}